<template>
  <v-combobox ref="input"
              class="app-combobox"
              v-model="computedValue"
              variant="outlined"
              :hide-details="hideDetails"
              density="compact"
              clear-icon="close"
              :color="color"
              autocomplete="off"
  >
    <template #item="{ item, props }">
      <slot name="item" :item="item" :props="props" />
    </template>
  </v-combobox>
</template>

<script>
/**
 * Default Combobox
 * @displayName AppCombobox
 */
/*
 * A wrapper around the combobox with default styling and custom default behavior
 * All Vuetify props for the combobox component are usable.
 * See doc : https://vuetifyjs.com/en/api/v-combobox/#props
 */
export default {
  name: 'AppCombobox',
  emits: ['input'],
  props: {
    /**
     * @model
     * Value of combobox
     */
    value: {
      type: [String, Object],
      default: undefined,
    },
    /**
     * Color variant of the combobox
     * @default blue darken-2
     */
    color: {
      default: 'blue darken-2',
    },
    /**
     * Hide space under combobox for detailed message
     * @default auto
     */
    hideDetails: {
      default: 'auto',
    },
  },
  computed: {
    computedValue: {
      get () {
        return this.value
      },
      /**
       * Gets called when the user type in combobox
       * @event input
       * @type String
       */
      set (newValue) {
        this.$emit('input', newValue)
      },
    },
  },
}
</script>

<style scoped lang="scss">
.app-combobox {
  :deep(.v-field) {
    --outline-color: rgb(var(--v-theme-border-secondary));
    --outline-width: 1px;
    --text-color: rgb(var(--v-theme-text-secondary));
    --background-color: rgb(var(--v-theme-surface-primary));
    --v-disabled-opacity: 1;
    --placeholder-color: rgb(var(--v-theme-text-placeholder));
    color: var(--text-color);
    background-color: var(--background-color);

    &:not(.v-field--disabled):hover {
      --outline-color: rgb(var(--v-theme-border-secondary-hover));
    }

    &:not(.v-field--disabled):focus-within {
      --outline-color: rgb(var(--v-theme-border-info-bold));
      --outline-width: 2px;
    }

    &.v-field--disabled {
      --text-color: rgb(var(--v-theme-text-disabled));
      --background-color: rgb(var(--v-theme-surface-disabled));
      --outline-color: rgb(var(--v-theme-border-disabled));
      --placeholder-color: rgb(var(--v-theme-text-disabled));
    }

    &.v-field--error {
      --outline-color: rgb(var(--v-theme-border-danger-bold)) !important;
    }
  }

  :deep(.v-field__input) {
    opacity: 1;
  }

  :deep(.v-field__input::placeholder) {
    color: var(--placeholder-color) !important;
    opacity: 1;
  }

  :deep(.v-field__outline__start),
  :deep(.v-field__outline__end), {
    border-color: var(--outline-color);
    border-top-width: var(--outline-width);
    border-bottom-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field__outline__notch::before) {
    border-color: var(--outline-color);
    border-top-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field--active .v-field__outline__notch::before) {
    opacity: 0;
  }

  :deep(.v-field__outline__notch::after) {
    border-color: var(--outline-color);
    border-bottom-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field__outline__start) {
    border-left-width: var(--outline-width);
  }

  :deep(.v-field__outline__end) {
    border-right-width: var(--outline-width);
  }
}
</style>
